<script>
// Shown after a drag-select across days on the calendar grid (no
// drag-to-move in v1 — see the Phase 3 plan). The grid has no per-agent
// axis, so the agent isn't implied by the selection: this defaults to the
// left panel's agent filter when one is active, otherwise the admin picks
// one here before either action is enabled.
import { format, parseISO } from 'date-fns';
import { SELECT_CLASS } from '../constants';

function displayDate(isoDate) {
  return format(parseISO(isoDate), 'd MMM yyyy');
}

export default {
  name: 'CreateChooser',
  props: {
    startDate: {
      type: String,
      required: true,
    },
    // Inclusive last day, YYYY-MM-DD — AgentCalendar already converts
    // FullCalendar's exclusive/timed selection end before passing it in.
    endDate: {
      type: String,
      required: true,
    },
    agents: {
      type: Array,
      default: () => [],
    },
    defaultAgentId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectClass: SELECT_CLASS,
      agentId: this.defaultAgentId,
    };
  },
  computed: {
    isSingleDay() {
      return this.endDate === this.startDate;
    },
    dateLabel() {
      if (this.isSingleDay) return displayDate(this.startDate);
      return this.$t('REAL_ESTATE.CALENDAR.CHOOSER_DATES', {
        start: displayDate(this.startDate),
        end: displayDate(this.endDate),
      });
    },
  },
  methods: {
    chooseAssignment() {
      if (!this.agentId) return;
      this.$emit('chooseAssignment', {
        agentId: this.agentId,
        startDate: this.startDate,
        endDate: this.endDate,
      });
    },
    chooseLeave() {
      if (!this.agentId) return;
      this.$emit('chooseLeave', {
        agentId: this.agentId,
        // Leave is a single date — the first day of the drag selection.
        date: this.startDate,
      });
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-sm"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-lg font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.CALENDAR.CHOOSER_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
        {{ dateLabel }}
      </p>

      <div class="mb-4">
        <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
          {{ $t('REAL_ESTATE.CALENDAR.CHOOSER_FIELD_AGENT') }}
        </label>
        <select v-model="agentId" :class="selectClass">
          <option value="" disabled>
            {{ $t('REAL_ESTATE.ASSIGNMENTS.FIELD_AGENT_PLACEHOLDER') }}
          </option>
          <option
            v-for="agent in agents"
            :key="agent.agent_id"
            :value="agent.agent_id"
          >
            {{ agent.name }}
          </option>
        </select>
      </div>

      <div class="space-y-2">
        <woot-button
          class="w-full"
          :disabled="!agentId"
          @click="chooseAssignment"
        >
          {{ $t('REAL_ESTATE.CALENDAR.CHOOSER_ASSIGNMENT') }}
        </woot-button>
        <woot-button
          class="w-full"
          color-scheme="secondary"
          :disabled="!agentId"
          @click="chooseLeave"
        >
          {{ $t('REAL_ESTATE.CALENDAR.CHOOSER_LEAVE') }}
        </woot-button>
        <woot-button class="w-full" variant="clear" @click="$emit('close')">
          {{ $t('REAL_ESTATE.CALENDAR.CHOOSER_CANCEL') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
